import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SHADOWS } from '../../styles/theme';
import { CheckCircle2, AlertTriangle } from './Icon';
import { GENERIC_ERROR_MESSAGE } from '../../constants/ErrorMessages';

export type ToastType = 'success' | 'error';

interface ToastApi {
  showToast: (message: string, type?: ToastType) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 3000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    clearTimer();
    setToast({ message: message?.trim() ? message : GENERIC_ERROR_MESSAGE, type });
    timerRef.current = setTimeout(() => setToast(null), AUTO_DISMISS_MS);
  }, []);

  const showSuccess = useCallback((m: string) => showToast(m, 'success'), [showToast]);
  const showError = useCallback((m: string) => showToast(m, 'error'), [showToast]);

  useEffect(() => clearTimer, []);

  const api = useMemo<ToastApi>(() => ({ showToast, showSuccess, showError }), [showToast, showSuccess, showError]);

  const visible = toast !== null;
  const isError = toast?.type === 'error';

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed', left: '50%', bottom: 16,
            transform: visible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(120%)',
            opacity: visible ? 1 : 0,
            transition: 'opacity .25s ease, transform .25s ease',
            zIndex: 10000,
            display: 'flex', alignItems: 'center', gap: 10,
            background: isError ? 'rgba(220,38,38,0.95)' : 'rgba(6,95,70,0.96)',
            color: '#fff', borderRadius: 12,
            padding: visible ? '12px 14px' : 0,
            boxShadow: SHADOWS.elevated,
            maxWidth: '92vw', width: 'max-content', minWidth: 240,
            pointerEvents: 'none',
          }}
        >
          {toast && (isError
            ? <AlertTriangle size={18} strokeWidth={1.75} aria-hidden />
            : <CheckCircle2 size={18} strokeWidth={1.75} aria-hidden />)}
          <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, flex: 1 }}>{toast?.message}</span>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
